import { parseISO } from "date-fns";
import { format, zonedTimeToUtc } from "date-fns-tz";
import { useMemo } from "react";

import { RenderHTMLContent } from "@/components/editor/render-html-content/render-html-content-component";
import logger from "@/utils/logger";

import { Typography } from "../../../typography/typography-component";
import {
  Container,
  EventDescription,
  EventName,
  EventTime,
  EventTimezone,
  Logo,
  RegisterButtonContainer,
} from "./event-card-styles";
import RelatedEventsRegisterButton from "./related-events-register-button";

type EventSlideProps = {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  logoURL: string;
  eventUrl: string;
  seriesExternalId: string;
};

export function EventCard(props: EventSlideProps) {
  const { seriesExternalId } = props;
  const eventSlug = props.eventUrl.split("/").pop();
  const [startTime, endTime, zone] = useMemo(() => {
    try {
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const startUtc = zonedTimeToUtc(parseISO(props.startTime), userTimeZone);
      const endUtc = zonedTimeToUtc(parseISO(props.endTime), userTimeZone);

      return [
        format(startUtc, "MMM d, h:mma", {
          timeZone: userTimeZone,
        }),
        format(endUtc, "MMM d, h:mma", {
          timeZone: userTimeZone,
        }),
        format(endUtc, "z", {
          timeZone: userTimeZone,
        }),
      ];
    } catch (error) {
      logger.error(error, {
        details: "Failed to parse start/end time in Related Events block",
      });

      return [];
    }
  }, [props.endTime, props.startTime]);

  return (
    <Container>
      <div className="mb-4 flex items-center">
        <div className="mr-4">
          <Logo image={props.logoURL} />
        </div>
        <div className="flex flex-col">
          <EventName title={props.title}>{props.title}</EventName>
          {startTime && endTime && (
            <EventTime>
              {startTime} - {endTime} <EventTimezone>{zone}</EventTimezone>
            </EventTime>
          )}
        </div>
      </div>
      <div className="mb-4 flex">
        <EventDescription title={props.description}>
          <Typography
            as={RenderHTMLContent}
            pattern="description"
            html={props.description}
          />
        </EventDescription>
      </div>
      <RegisterButtonContainer>
        <RelatedEventsRegisterButton
          seriesExternalId={seriesExternalId}
          eventSlug={eventSlug}
        />
      </RegisterButtonContainer>
    </Container>
  );
}
